import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from '../../redux/store';
import useAuth from 'src/hooks/useAuth';

import Iconify from 'src/components/Iconify';

import { Button } from 'src/@/components/ui/button';
import { Card } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from 'src/@/components/ui/dropdown-menu';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from 'src/@/components/ui/dialog';
import { toast } from 'src/@/components/ui/use-toast';

import campanhasService from 'src/services/campanhasService';
import actionListaClientesCanais from 'src/actions/clientes/actionListaClientesCanais';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import { filtrarCanaisCampanha } from 'src/utils/canaisDisparo';
import { hasFeatureAccess, FEATURE_KEYS } from 'src/utils/functions';
import v2_actionConfigCrm from 'src/actions/v2/crm/v2_actionConfigCrm';
import v2_actionListarListas from 'src/actions/v2/contatos/v2_actionListarListas';
import { getListaOptionsTags } from 'src/redux/slices/global';

import { CAMPANHAS_DESIGN as D } from './Campanhas.design';
import CampanhasTabela from './campanhas/CampanhasTabela';
import NovaCampanhaWizard from './campanhas/NovaCampanhaWizard';

const TAB_KEYS = [
    { key: 'todas', labelKey: 'campaigns.tabs.all', status: null },
    { key: 'agendado', labelKey: 'campaigns.tabs.scheduled', status: 'agendado' },
    { key: 'andamento', labelKey: 'campaigns.tabs.inProgress', status: 'em andamento' },
    { key: 'finalizado', labelKey: 'campaigns.tabs.finished', status: 'finalizado' },
    { key: 'erro', labelKey: 'campaigns.tabs.error', status: 'erro' },
];

// A lista so faz polling quando ha algo realmente em voo. Websocket nao serve como
// mecanismo primario aqui: e bloqueado em alguns navegadores dos usuarios.
const INTERVALO_POLL_MS = 20000;

export default function Campanhas() {
    const { t } = useTranslation('marketing');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, cliente } = useAuth();
    const { optionsTags } = useSelector((state) => state.global);
    const [searchParams, setSearchParams] = useSearchParams();

    const isMultisession = cliente.multiSession === 1;
    const podeUsarSms = hasFeatureAccess(cliente, user, FEATURE_KEYS.SMS);

    const [campanhas, setCampanhas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);

    const [busca, setBusca] = useState('');
    const [filtroTipoEnvio, setFiltroTipoEnvio] = useState('todos');

    useEffect(() => {
        if (!podeUsarSms && filtroTipoEnvio === 'sms') {
            setFiltroTipoEnvio('todos');
        }
    }, [podeUsarSms, filtroTipoEnvio]);

    const [clienteIdPadrao, setClienteIdPadrao] = useState(user.cliente_id_padrao || '');

    const [canais, setCanais] = useState([]);
    const [configCrm, setConfigCrm] = useState(null);
    const [contactLists, setContactLists] = useState([]);

    const [wizardAberto, setWizardAberto] = useState(false);
    const [campanhaParaEditar, setCampanhaParaEditar] = useState(null);
    const [tipoEnvioInicial, setTipoEnvioInicial] = useState('whatsapp');
    const [paraExcluir, setParaExcluir] = useState(null);

    const abaAtiva = searchParams.get('tab') || 'todas';
    const statusAba = TAB_KEYS.find((tab) => tab.key === abaAtiva)?.status || null;

    const tabs = useMemo(
        () => TAB_KEYS.map((tab) => ({ ...tab, label: t(tab.labelKey) })),
        [t]
    );

    const clienteAlvo = isMultisession ? clienteIdPadrao : cliente._id;

    // Refs para o polling nao precisar se re-registrar a cada tecla digitada.
    const filtrosRef = useRef({});
    filtrosRef.current = { status: statusAba, tipo_envio: filtroTipoEnvio, q: busca, clienteAlvo };

    const carregar = useCallback(async ({ silencioso = false } = {}) => {
        const { clienteAlvo: alvo, status, tipo_envio: tipoEnvio, q } = filtrosRef.current;
        if (!alvo) return;

        if (!silencioso) setLoading(true);
        try {
            const resposta = await campanhasService.listar(alvo, {
                status: status || undefined,
                tipo_envio: tipoEnvio !== 'todos' ? tipoEnvio : undefined,
                q: q || undefined,
                limit: 100,
            });
            setCampanhas(resposta.data.campanhas || []);
            setErro(null);
        } catch (err) {
            if (!silencioso) setErro(t('campaigns.toast.loadFailed'));
        } finally {
            if (!silencioso) setLoading(false);
        }
    }, [t]);

    // Escolher um pipeline no wizard refaz esta busca com ?pipeline_id=..., e os
    // estagios daquele pipeline chegam em configCrm.stages.
    const buscarConfigCrm = useCallback(
        (querys) => {
            v2_actionConfigCrm(cliente._id, querys)
                .then((res) => { if (res.data.data) setConfigCrm(res.data.data); })
                .catch(() => {});
        },
        [cliente._id]
    );

    // Carga inicial dos dados auxiliares.
    useEffect(() => {
        if (isMultisession) {
            actionListaClientesCanais(cliente._id)
                .then((res) => {
                    const lista = filtrarCanaisCampanha(res.data.data || []);
                    setCanais(lista);
                    if (lista.length > 0) {
                        setClienteIdPadrao((atual) => (
                            lista.some((c) => c.cliente_id === atual) ? atual : lista[0].cliente_id
                        ));
                    }
                })
                .catch(() => {});
        } else {
            actionListaCanaisPorCliente(cliente._id)
                .then((res) => {
                    setCanais(filtrarCanaisCampanha(res.data.data || []));
                })
                .catch(() => {});
        }
        buscarConfigCrm(null);
        v2_actionListarListas(cliente._id)
            .then((res) => setContactLists(res.data.data || []))
            .catch(() => {});
        dispatch(getListaOptionsTags(cliente._id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recarrega quando qualquer filtro muda. A busca entra com debounce para nao
    // disparar uma requisicao por tecla.
    useEffect(() => {
        const timer = setTimeout(() => carregar(), busca ? 400 : 0);
        return () => clearTimeout(timer);
    }, [carregar, clienteAlvo, statusAba, filtroTipoEnvio, busca]);

    const temEmAndamento = useMemo(
        () => campanhas.some((c) => c.status_envio === 'em andamento'),
        [campanhas]
    );

    useEffect(() => {
        if (!temEmAndamento) return undefined;
        const id = setInterval(() => {
            // Aba em segundo plano nao precisa de dado fresco.
            if (document.visibilityState !== 'visible') return;
            carregar({ silencioso: true });
        }, INTERVALO_POLL_MS);
        return () => clearInterval(id);
    }, [temEmAndamento, carregar]);

    const abrirWizard = (tipoEnvio) => {
        setCampanhaParaEditar(null);
        setTipoEnvioInicial(tipoEnvio === 'sms' && !podeUsarSms ? 'whatsapp' : tipoEnvio);
        setWizardAberto(true);
    };

    const handleEditar = (campanha) => {
        setCampanhaParaEditar(campanha);
        setTipoEnvioInicial(campanha.tipo_envio || 'whatsapp');
        setWizardAberto(true);
    };

    const handleSalvo = (canalUsado) => {
        setWizardAberto(false);
        setCampanhaParaEditar(null);
        if (isMultisession && canalUsado && canalUsado !== clienteIdPadrao) {
            setClienteIdPadrao(canalUsado);
        } else {
            carregar();
        }
    };

    const handleExcluir = async () => {
        try {
            await campanhasService.descontinuar(paraExcluir._id);
            setParaExcluir(null);
            carregar();
            toast({ title: t('common.success'), description: t('campaigns.toast.deleted') });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error?.mensagem || t('campaigns.toast.deleteFailed'),
                variant: 'destructive',
            });
        }
    };

    return (
        <div className={D.page.root} style={{ fontFamily: D.page.fontStack }}>
            <div className={D.page.stickyHeader}>
                <div className={D.page.container}>
                    <div className={D.page.topbar}>
                        <div className={D.page.topbarIdentity}>
                            <div className={D.page.iconTile}>
                                <Iconify icon="mdi:bullhorn-outline" width={22} />
                            </div>
                            <div className="min-w-0">
                                <h1 className={D.page.headerTitle}>{t('campaigns.title')}</h1>
                                <p className={D.page.headerSubtitle}>{t('campaigns.subtitle')}</p>
                            </div>
                        </div>
                        <div className={D.page.topbarActions}>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button type="button" className={D.page.primaryButton}>
                                        <Iconify icon="eva:plus-fill" width={16} />
                                        {t('campaigns.new')}
                                        <Iconify icon="eva:chevron-down-fill" width={14} />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-[268px] p-1">
                                    <DropdownMenuLabel className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2">
                                        {t('campaigns.newMenu.label')}
                                    </DropdownMenuLabel>
                                    <OpcaoNovaCampanha
                                        icone="mdi:whatsapp"
                                        titulo={t('campaigns.filters.whatsapp')}
                                        descricao={t('campaigns.newMenu.whatsappDesc')}
                                        destaque
                                        onClick={() => abrirWizard('whatsapp')}
                                    />
                                    {podeUsarSms && (
                                        <OpcaoNovaCampanha
                                            icone="mdi:message-text-outline"
                                            titulo={t('campaigns.filters.sms')}
                                            descricao={t('campaigns.newMenu.smsDesc')}
                                            badge={t('campaigns.newMenu.smsBadge')}
                                            onClick={() => abrirWizard('sms')}
                                        />
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`${D.page.container} pt-4 ${D.page.pageBody}`}>
                <div className={D.page.filterBar}>
                    {isMultisession && (
                        <div className={D.page.filterItem}>
                            <span className={D.page.filterLabel}>{t('campaigns.filters.channel')}</span>
                            <Select value={clienteIdPadrao} onValueChange={setClienteIdPadrao}>
                                <SelectTrigger className={D.page.filterSelect}>
                                    <SelectValue placeholder={t('campaigns.filters.select')} />
                                </SelectTrigger>
                                <SelectContent className="z-[9999]" position="popper" sideOffset={4}>
                                    {canais.map((c) => (
                                        <SelectItem key={c.cliente_id} value={c.cliente_id}>
                                            {c.nome}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className={D.page.filterItem}>
                        <span className={D.page.filterLabel}>{t('campaigns.filters.type')}</span>
                        <Select value={filtroTipoEnvio} onValueChange={setFiltroTipoEnvio}>
                            <SelectTrigger className={D.page.filterSelect}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="z-[9999]" position="popper" sideOffset={4}>
                                <SelectItem value="todos">{t('campaigns.filters.typeAll')}</SelectItem>
                                <SelectItem value="whatsapp">{t('campaigns.filters.whatsapp')}</SelectItem>
                                {podeUsarSms && (
                                    <SelectItem value="sms">{t('campaigns.filters.sms')}</SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className={D.page.filterItem}>
                        <span className={D.page.filterLabel}>{t('campaigns.filters.search')}</span>
                        <div className={D.page.filterSearchWrap}>
                            <Iconify icon="eva:search-outline" width={15} className={D.page.searchIcon} />
                            <Input
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                placeholder={t('campaigns.searchPlaceholder')}
                                className={D.page.filterSearch}
                            />
                        </div>
                    </div>
                </div>

                <div className={D.page.tabsBar}>
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setSearchParams(tab.key === 'todas' ? {} : { tab: tab.key })}
                            className={`${D.page.tabBtn} ${
                                abaAtiva === tab.key ? D.page.tabBtnActive : D.page.tabBtnIdle
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {loading && (
                    <Card className={D.estado.loadingCard}>
                        <div className={D.estado.loadingBox}>
                            <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin mb-4 text-[#7C3AED]" />
                            <p className={D.estado.loadingTexto}>{t('campaigns.loading')}</p>
                        </div>
                    </Card>
                )}

                {!loading && erro && (
                    <Card className={D.estado.loadingCard}>
                        <div className={D.estado.vazioBox}>
                            <div className={D.estado.vazioIcone}>
                                <Iconify icon="mdi:alert-circle-outline" width={28} className="text-[#7C3AED]" />
                            </div>
                            <h3 className={D.estado.vazioTitulo}>{erro}</h3>
                            <Button variant="link" onClick={() => carregar()} className="text-[#7C3AED] font-medium">
                                {t('common.retry')}
                            </Button>
                        </div>
                    </Card>
                )}

                {!loading && !erro && campanhas.length === 0 && (
                    <Card className={D.estado.loadingCard}>
                        <div className={D.estado.vazioBox}>
                            <div className={D.estado.vazioIcone}>
                                <Iconify icon="mdi:bullhorn-outline" width={28} className="text-[#7C3AED]" />
                            </div>
                            <h3 className={D.estado.vazioTitulo}>{t('campaigns.empty.title')}</h3>
                            <p className={D.estado.vazioTexto}>
                                {busca || statusAba || filtroTipoEnvio !== 'todos'
                                    ? t('campaigns.empty.filtered')
                                    : t('campaigns.empty.default')}
                            </p>
                            {!busca && !statusAba && filtroTipoEnvio === 'todos' && (
                                <Button
                                    variant="link"
                                    onClick={() => abrirWizard('whatsapp')}
                                    className="text-[#7C3AED] font-medium"
                                >
                                    {t('campaigns.empty.action')}
                                </Button>
                            )}
                        </div>
                    </Card>
                )}

                {!loading && !erro && campanhas.length > 0 && (
                    <CampanhasTabela
                        campanhas={campanhas}
                        onAcompanhar={(campanha) => navigate(`/dashboard/marketing/campanhas/${campanha._id}`)}
                        onEditar={handleEditar}
                        onExcluir={setParaExcluir}
                    />
                )}
            </div>

            <NovaCampanhaWizard
                open={wizardAberto}
                onClose={() => { setWizardAberto(false); setCampanhaParaEditar(null); }}
                onSalvo={handleSalvo}
                campanhaParaEditar={campanhaParaEditar}
                tipoEnvioInicial={tipoEnvioInicial}
                podeUsarSms={podeUsarSms}
                canais={canais}
                isMultisession={isMultisession}
                clienteIdPadrao={clienteIdPadrao}
                configCrm={configCrm}
                optionsTags={optionsTags}
                contactLists={contactLists}
                onSelecionarPipeline={(pipelineId) => buscarConfigCrm(`?pipeline_id=${pipelineId}`)}
            />

            <Dialog open={!!paraExcluir} onOpenChange={(v) => !v && setParaExcluir(null)}>
                <DialogContent className="max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>{t('campaigns.delete.title')}</DialogTitle>
                        <DialogDescription>
                            {t('campaigns.delete.description', { name: paraExcluir?.nome })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setParaExcluir(null)} className="text-xs">
                            {t('common.cancel')}
                        </Button>
                        <Button variant="destructive" onClick={handleExcluir} className="text-xs">
                            {t('common.delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/** Espelha o OpcaoNovaProposta de Propostas: chip + titulo + descricao + chevron. */
function OpcaoNovaCampanha({ icone, titulo, descricao, badge, destaque, onClick }) {
    return (
        <DropdownMenuItem
            className="cursor-pointer rounded-lg px-1.5 py-1.5 focus:bg-violet-50/70 dark:focus:bg-violet-500/10 group"
            onClick={onClick}
        >
            <div className="flex items-center gap-2.5 w-full min-w-0">
                <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                        destaque
                            ? 'bg-violet-100/70 dark:bg-violet-500/15 text-[#7C3AED]'
                            : 'bg-gray-100 dark:bg-gray-500/10 text-gray-500 dark:text-gray-400 group-hover:bg-violet-100/70 group-hover:text-[#7C3AED] dark:group-hover:bg-violet-500/15'
                    }`}
                >
                    <Iconify icon={icone} width={14} />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-medium text-gray-900 dark:text-[#D1D2D3] leading-tight flex items-center gap-1.5">
                        {titulo}
                        {badge && (
                            <span className="px-1 py-px rounded bg-[#7C3AED] text-white text-[8px] font-bold uppercase tracking-wider leading-normal">
                                {badge}
                            </span>
                        )}
                    </p>
                    <p className="text-[10px] text-gray-400 leading-tight mt-0.5 truncate">{descricao}</p>
                </div>
                <Iconify
                    icon="eva:arrow-ios-forward-fill"
                    width={13}
                    className="text-gray-300 dark:text-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                />
            </div>
        </DropdownMenuItem>
    );
}
